import { MAX_STICKER_TRANSPARENCY, MIN_STICKER_TRANSPARENCY } from '../constants'

export const isSticker = (imageData: ImageData): boolean => {
    const data = imageData.data
    const totalPixels = data.length / 4
    let transparentPixels = 0

    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) {
            transparentPixels++
        }
    }

    return transparentPixels / totalPixels >= MIN_STICKER_TRANSPARENCY
}

export const isAGoodSticker = (imageData: ImageData): boolean => {
    const data = imageData.data
    const totalPixels = data.length / 4
    let transparentPixels = 0

    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) {
            transparentPixels++
        }
    }

    const percentageTransparentPixels = transparentPixels / totalPixels

    return (
        percentageTransparentPixels >= MIN_STICKER_TRANSPARENCY &&
        percentageTransparentPixels < MAX_STICKER_TRANSPARENCY
    )
}

/**
 * Remove all transparent or semi-transparent pixels.
 * @param imageData Image data to modify.
 */
export const removeTransparency = (imageData: ImageData): ImageData => {
    const data = imageData.data
    for (let i = 0; i < data.length; i += 4) {
        // remove all semi-transparent pixels
        if (data[i + 3] > 0 && data[i + 3] < 255) {
            data[i + 3] = 0
        }
    }
    return imageData
}

type HueRotateMatrix = {
    a00: number
    a01: number
    a02: number
    a10: number
    a11: number
    a12: number
    a20: number
    a21: number
    a22: number
}
const hueRotateMatrix = (rotation: number): HueRotateMatrix => {
    const cosV = Math.cos(rotation)
    const sinV = Math.sin(rotation)

    const a00 = 0.213 + cosV * 0.787 + sinV * -0.213
    const a10 = 0.213 + cosV * -0.213 + sinV * 0.143
    const a20 = 0.213 + cosV * -0.213 + sinV * -0.787

    const a01 = 0.715 + cosV * -0.715 + sinV * -0.715
    const a11 = 0.715 + cosV * 0.285 + sinV * 0.14
    const a21 = 0.715 + cosV * -0.715 + sinV * 0.715

    const a02 = 0.072 + cosV * -0.072 + sinV * 0.928
    const a12 = 0.072 + cosV * -0.072 + sinV * -0.283
    const a22 = 0.072 + cosV * 0.928 + sinV * 0.072

    return { a00, a10, a20, a01, a11, a21, a02, a12, a22 }
}

/**
 * Rotates image data hue by the specified degrees.
 * @param imageData Image data to modify.
 * @param degrees Degree to rotate colors by.
 */
export const hueRotate = (imageData: ImageData, degrees: number): ImageData => {
    const data = imageData.data
    const radians = degrees * (Math.PI / 180)
    const matrix = hueRotateMatrix(radians)

    for (let i = 0; i < data.length; i += 4) {
        const r = matrix.a00 * data[i] + matrix.a01 * data[i + 1] + matrix.a02 * data[i + 2]
        const g = matrix.a10 * data[i] + matrix.a11 * data[i + 1] + matrix.a12 * data[i + 2]
        const b = matrix.a20 * data[i] + matrix.a21 * data[i + 1] + matrix.a22 * data[i + 2]
        data[i] = r
        data[i + 1] = g
        data[i + 2] = b
    }

    return imageData
}

/**
 * Converts image data to grayscale.
 * @param imageData Image data to modify.
 */
export const grayscale = (imageData: ImageData): ImageData => {
    const data = imageData.data

    for (let i = 0; i < data.length; i += 4) {
        const r = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]
        const g = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]
        const b = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]
        data[i] = r
        data[i + 1] = g
        data[i + 2] = b
    }

    return imageData
}
